import type { JSX } from '@builder.io/qwik';
import { cva } from 'class-variance-authority';
import { component$, useSignal, useVisibleTask$ } from '@builder.io/qwik';
import { Logger } from '~/utilities/logger';
import { useBlockClasses } from '~/utilities/useBlockClasses';

const cssClasses = cva('my-[18px] [&>iframe]:max-w-full flex justify-center', {
  variants: {
    type: {
      iframe: '',
      other: '',
      youtube:
        'relative aspect-video overflow-hidden max-w-full bg-black [&>iframe]:absolute [&>iframe]:top-0 [&>iframe]:left-0 [&>iframe]:w-full [&>iframe]:h-full [&>iframe]:border-0',
      vimeo:
        'relative aspect-video overflow-hidden max-w-full bg-black [&>iframe]:absolute [&>iframe]:top-0 [&>iframe]:left-0 [&>iframe]:w-full [&>iframe]:h-full [&>iframe]:border-0',
      instagram: '',
      facebook: '',
      riddle: 'pt-5 px-5 bg-lightGrey relative max-w-full',
    },
  },
});

export type EmbedType = {
  embed: string;
  type:
    | 'iframe'
    | 'youtube'
    | 'vimeo'
    | 'instagram'
    | 'facebook'
    | 'riddle'
    | 'other';
  style?: Record<string, string>;
  loadScript?: string;
  isLoadedProp?:
    | 'mmInstagramLoaded'
    | 'mmFacebookLoaded'
    | 'mmRiddleLoaded'
    | 'mmDisqusLoaded';
  height?: string;
  innerHtml?: boolean;
  wrapper?: 'div';
  wrapperClass?: string;
  timeout?: number;
};

export default component$<EmbedType>(
  ({
    embed,
    type,
    style = {},
    loadScript,
    isLoadedProp,
    innerHtml,
    wrapper,
    wrapperClass,
    timeout,
  }): JSX.Element => {
    const embedHTML = useSignal('');
    const blockClasses = useBlockClasses();

    // This uses an intersection observer on the client side to update the signal and load the iframe
    // when it is scrolled into view
    // Throws a linter warning because it can be misused, but this is a good use case for it
    // eslint-disable-next-line qwik/no-use-visible-task
    useVisibleTask$(() => {
      embedHTML.value = embed;
      if (loadScript) {
        if (isLoadedProp) {
          if (window[isLoadedProp]) {
            // TODO: Can we move these to the parent components while getting around the serializable requirement?
            setTimeout(() => {
              switch (type) {
                case 'instagram':
                  if (window.instgrm?.Embeds) {
                    window.instgrm.Embeds.process();
                  }
                  break;
                case 'facebook':
                  if (window.FB?.XFBML) {
                    window.FB.XFBML.parse();
                  }
                  break;
              }
            }, 500);

            return;
          }
          window[isLoadedProp] = true;
        }

        const script = document.createElement('script');

        if (innerHtml) {
          script.innerHTML = loadScript;
        } else {
          script.src = loadScript;
          script.async = true;
        }

        // Set a timeout to give certain scripts (like Disqus) time to update the DOM before executing JS functions
        setTimeout(() => {
          document.head.appendChild(script);
        }, timeout ?? 100);
      }
    });

    if (!embedHTML.value) {
      if (!embed) {
        new Logger().error('Embed HTML not found');
      }
      // Return an empty div so that useVisibleTask is actually triggered
      return <div />;
    }

    if (wrapper === 'div') {
      return (
        <div class={wrapperClass} dangerouslySetInnerHTML={embedHTML.value} />
      );
    }

    return (
      <figure
        style={style}
        class={cssClasses({ type, class: blockClasses })}
        dangerouslySetInnerHTML={embedHTML.value}
      />
    );
  }
);
